<script>
  /**
   * A tenant's readiness checklist, aggregated server-side from every enabled
   * plugin. Actions that change something run here; navigation actions are
   * handed to the parent via `onNavigate(actionId)`.
   */
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import ConfirmModal from '../ConfirmModal.svelte';

  let { tenant, onNavigate } = $props();

  let loading = $state(true);
  let error = $state(null);
  let data = $state(null);
  let live = $state(false);
  let busy = $state(false);

  let lifecycleOpen = $state(false);
  let lifecycleStatus = $state('ready');
  let lifecycleReason = $state('');

  const plugins = {
    strao_portal: 'Portal', hostaway: 'Hostaway', str_autoresponder: 'Auto-responder', tally: 'Tally',
  };
  const icon = {
    ok: ['fa-circle-check', '--p-success'],
    warn: ['fa-triangle-exclamation', '--p-warning'],
    fail: ['fa-circle-xmark', '--p-danger'],
    unknown: ['fa-circle-question', '--p-text-faint'],
  };

  async function load(withLive = false) {
    loading = true;
    error = null;
    try {
      data = await api.getTenantReadiness(tenant.id, withLive);
      live = withLive;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { tenant.id; tenant.is_active; load(false); });

  const groups = $derived.by(() => {
    const out = {};
    for (const check of data?.checks ?? []) (out[check.plugin] ??= []).push(check);
    return Object.entries(out);
  });

  async function act(action) {
    if (action.id === 'create_autoresponder_config') {
      busy = true;
      try {
        await api.createTenantAutoresponderConfig(tenant.id);
        toast.success('Created a default (disabled) auto-responder config');
        await load(live);
      } catch (e) { toast.error(e.message); } finally { busy = false; }
    } else if (action.id === 'register_webhook') {
      busy = true;
      try {
        const r = await api.registerTenantWebhook(tenant.id);
        toast.success(`Webhook: ${r.state}`);
        await load(true);
      } catch (e) { toast.error(e.message); } finally { busy = false; }
    } else if (action.id === 'set_lifecycle') {
      lifecycleOpen = true;
    } else {
      onNavigate?.(action.id);
    }
  }

  async function applyLifecycle() {
    busy = true;
    try {
      const r = await api.setTenantLifecycle(tenant.id, lifecycleStatus, lifecycleReason.trim());
      toast.success(`Lifecycle ${r.before} → ${r.after}`);
      lifecycleOpen = false;
      lifecycleReason = '';
      await load(live);
    } catch (e) { toast.error(e.message); } finally { busy = false; }
  }
</script>

<section class="space-y-4">
  <div class="flex flex-wrap items-center justify-between gap-2">
    <div>
      <h2 class="text-sm font-semibold">Readiness</h2>
      {#if data}
        <p class="text-xs p-faint">
          {data.summary.ok} ok · {data.summary.warn} warning · {data.summary.fail} failing
          {#if data.summary.unknown}· {data.summary.unknown} not checked{/if}
        </p>
      {/if}
    </div>
    <div class="flex gap-2">
      <button type="button" class="p-btn p-btn-quiet" disabled={loading} onclick={() => load(live)}>Refresh</button>
      <button type="button" class="p-btn p-btn-primary" disabled={loading}
              title="Asks Hostaway and Tally directly — a few seconds"
              onclick={() => load(true)}>Run live checks</button>
    </div>
  </div>

  {#if error}
    <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
  {:else if loading && !data}
    <p class="p-card p-4 text-sm p-muted">Checking…</p>
  {:else}
    {#each groups as [plugin, checks]}
      <div class="p-card divide-y" style="border-color: var(--p-border)">
        <p class="px-4 py-2 text-xs font-semibold uppercase tracking-wide p-muted">{plugins[plugin] ?? plugin}</p>
        {#each checks as check (check.key)}
          <div class="flex items-start justify-between gap-4 px-4 py-3 text-sm">
            <div class="flex gap-3">
              <i class="fas {icon[check.status][0]} mt-0.5" style="color: var({icon[check.status][1]})"
                 aria-label={check.status}></i>
              <div>
                <p>{check.label}</p>
                <p class="text-xs p-faint">{check.detail}</p>
              </div>
            </div>
            {#if check.action}
              <button type="button" class="p-btn p-btn-quiet whitespace-nowrap px-2 py-1 text-xs"
                      disabled={busy} onclick={() => act(check.action)}>{check.action.label}</button>
            {/if}
          </div>
        {/each}
      </div>
    {/each}
  {/if}
</section>

<ConfirmModal open={lifecycleOpen}
              title="Change onboarding lifecycle"
              message="Real-time guest messages are processed only at Ready or Operational. Drafts still require the auto-responder to be switched on."
              confirmLabel="Change lifecycle"
              variant="primary"
              confirmDisabled={!lifecycleReason.trim() || busy}
              onConfirm={applyLifecycle}
              onCancel={() => { lifecycleOpen = false; lifecycleReason = ''; }}>
  {#snippet children()}
    <label for="lc-status" class="mt-4 block text-xs font-medium p-muted">New status</label>
    <select id="lc-status" class="p-input mt-1" bind:value={lifecycleStatus}>
      {#each ['not_started', 'importing', 'training', 'ready', 'operational', 'paused'] as s}
        <option value={s}>{s}</option>
      {/each}
    </select>
    <label for="lc-reason" class="mt-3 block text-xs font-medium p-muted">Reason (required)</label>
    <input id="lc-reason" class="p-input mt-1" bind:value={lifecycleReason} maxlength="500" />
  {/snippet}
</ConfirmModal>
